<script setup lang="ts">
import IconBarcode from '~icons/mdi/barcode-scanner'
import IconMagnify from '~icons/mdi/magnify'
import type { SearchSuggestion, SuggestionType } from '~/types'

const display = useDisplay()
const searchStore = useSearchStore()
const route = useRoute()
const localePath = useLocalePath()
const { t } = useI18n()

const search = ref('')
const {
  suggestionItems,
  resetSearchSuggestions,
} = useSearchSuggestions(search)

const selectedBySearch = ref<SearchSuggestion | null>(null)
const isNavigatingFromSearch = ref(false)
const isCleared = ref(false)

const selected = computed(() => {
  if (isCleared.value) return null

  const path = route.path
  if (path === localePath('/search')) {
    const q = route.query.q as string | undefined
    return q ? { label: q, value: q, type: 'product' as SuggestionType } : null
  }
  return selectedBySearch.value
})

watch(() => route.fullPath, () => {
  isCleared.value = false
  if (!isNavigatingFromSearch.value) {
    selectedBySearch.value = null
  }
  isNavigatingFromSearch.value = false
  search.value = ''
  resetSearchSuggestions()
})
const searchRef = ref()
const isFocused = ref(false)

const isBarcodeVisible = computed(() => !search.value && !selected.value)

// Initialize
searchStore.getSearchHistory()

// Helpers
const escapeRegex = (str: string) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

function highlightMatch(text: string, query: string): string {
  if (!query || !text) return text
  const regex = new RegExp(`(${escapeRegex(query)})`, 'gi')
  return text.replace(regex, '<strong>$1</strong>')
}

function getTypeLabel(type: SuggestionType): string {
  if (type === 'supplier') return t('layout.search.suggestion.supplier')
  if (type === 'category') return t('layout.search.suggestion.category')
  return ''
}

// Search & Selection
async function onSelect(item: SearchSuggestion | string) {
  if (!item) return

  // Handle free text search (enter key or custom value)
  if (typeof item === 'string') {
    await navigateTo({ path: localePath('search'), query: { q: item } })
    searchRef.value?.blur()
    return
  }

  // Handle suggestion selection
  searchRef.value?.blur()

  if ((item.type === 'supplier' || item.type === 'category') && item.url) {
    selectedBySearch.value = item
    isNavigatingFromSearch.value = true
    await navigateTo(localePath(item.url))
  }
  else {
    await navigateTo({ path: localePath('search'), query: { q: item.value } })
  }
}

function onEnter() {
  if (search.value) {
    onSelect(search.value)
  }
}

function onClear() {
  search.value = ''
  selectedBySearch.value = null
  isCleared.value = true
  resetSearchSuggestions()
}

function onFocused(focused: boolean) {
  isFocused.value = focused
}
</script>

<template>
  <v-autocomplete
    id="search-input"
    ref="searchRef"
    v-model:search="search"
    :model-value="selected"
    :items="suggestionItems"
    :placeholder="display.smAndDown.value && !isFocused ? $t('layout.search.placeholder.short') : $t('layout.search.placeholder.full')"
    :class="[
      display.smAndDown.value ? 'mx-4 my-0' : 'mx-12 my-0',
      { 'search-bar': isBarcodeVisible },
    ]"
    item-title="label"
    item-value="value"
    :filter-keys="['label', 'value']"
    :no-filter="true"
    clearable
    hide-details
    hide-no-data
    hide-selected
    return-object
    rounded="xl"
    variant="solo"
    density="compact"
    bg-color="white"
    color="black"
    transition="slide-y-transition"
    max-width="800"
    :prepend-inner-icon="IconMagnify"
    :menu-props="{ maxWidth: '100%' }"
    data-test-id="search-bar-input"
    @update:model-value="onSelect"
    @update:focused="onFocused"
    @click:clear="onClear"
    @keyup.enter="onEnter"
  >
    <template #item="{ item, props }">
      <v-list-item
        v-bind="props"
        :title="undefined"
        @click="onSelect(item.raw)"
      >
        <template v-if="item.raw.type === 'supplier' && item.raw.imageUrl" #prepend>
          <NuxtImg
            width="32"
            height="32"
            class="me-2"
            :src="item.raw.imageUrl"
            :alt="item.raw.label"
          />
        </template>

        <v-list-item-title>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <span v-html="highlightMatch(item.raw.label, search)" />
        </v-list-item-title>

        <v-list-item-subtitle v-if="item.raw.subtitle" class="text-caption">
          {{ item.raw.subtitle }}
        </v-list-item-subtitle>

        <template #append>
          <span
            v-if="['supplier', 'category'].includes(item.raw.type)"
            class="text-caption text-grey ms-2"
          >
            {{ getTypeLabel(item.raw.type) }}
          </span>
        </template>
      </v-list-item>
    </template>

    <template #append>
      <v-btn
        v-if="display.smAndUp.value"
        id="search-button"
        data-test-id="search-button"
        rounded="xl"
        color="white"
        variant="outlined"
        elevation="2"
        height="40"
        :class="display.lgAndUp.value ? 'ms-1 px-6' : 'ms-1 px-4'"
        class="bg-green-500"
        @click="onEnter"
      >
        {{ $t('layout.search.placeholder.short') }}
      </v-btn>
    </template>

    <template #append-inner>
      <v-btn
        v-if="isBarcodeVisible"
        id="barcode-button"
        aria-label="Barcode"
        variant="flat"
        class="pa-0"
        :to="localePath('barcode')"
        @mousedown.stop
        @click.stop
      >
        <v-icon :size="display.lgAndUp.value ? 28 : 24">
          <IconBarcode />
        </v-icon>
      </v-btn>
    </template>
  </v-autocomplete>
</template>

<style lang="scss">
.search-bar {
  .v-icon {
    color: rgb(var(--v-theme-black-800))
  }

  .v-field--focused {
    color: rgb(var(--v-theme-black-800)) !important;
    .v-icon {
      opacity: .7;
      color: rgb(var(--v-theme-black-800));
    }
  }
  .v-field--variant-solo.v-field--focused .v-field__overlay{
    background-color: white;
    color: rgba(0,0,0,.87) !important;
  }
}
div.v-input__append {
  margin:0 !important;
}

.search-bar .v-field__append-inner > i {
  display: none !important;
}
</style>
